import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { colors, typography } from '../../styles/theme';

const DEFAULT_CONTROL_HEIGHT = 40;
const DEFAULT_MENU_MAX_HEIGHT = 240;
const VIEWPORT_PADDING = 12;
/** Búsqueda + footer + separación del trigger (aprox.). */
const MENU_CHROME_HEIGHT = 96;
const MENU_LIST_MIN_HEIGHT = 120;

const POPOVER_POPPER_SLOT_PROPS = {
  popper: {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
          padding: VIEWPORT_PADDING,
          altAxis: true,
        },
      },
      { name: 'flip', enabled: true },
    ],
  },
};

const getAvailableMenuListHeight = (anchorEl, preferredListHeight) => {
  if (!anchorEl || typeof window === 'undefined') return preferredListHeight;

  const rect = anchorEl.getBoundingClientRect();
  const placementGap = 8;
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING - placementGap - MENU_CHROME_HEIGHT;
  const spaceAbove = rect.top - VIEWPORT_PADDING - placementGap - MENU_CHROME_HEIGHT;
  const available = Math.max(spaceBelow, spaceAbove);

  return Math.max(
    MENU_LIST_MIN_HEIGHT,
    Math.min(preferredListHeight, Math.floor(available)),
  );
};

const checkboxSx = {
  p: 0.5,
  mr: 1,
  color: 'rgba(255,255,255,0.35)',
  '&.Mui-checked': { color: colors.primary },
};

const optionRowSx = (selected) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  px: 1.5,
  py: 0.875,
  cursor: 'pointer',
  bgcolor: selected ? 'rgba(72,146,145,0.12)' : 'transparent',
  '&:hover': { bgcolor: selected ? 'rgba(72,146,145,0.16)' : 'rgba(255,255,255,0.06)' },
});

/**
 * Select múltiple con búsqueda interna, menú acotado en alto y mismo ancho que el trigger.
 *
 * @param {object} props
 * @param {string} [props.id]
 * @param {{ value: string, label: string }[]} props.options
 * @param {string[]} props.value
 * @param {(next: string[]) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {string} [props.searchPlaceholder]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.loading]
 * @param {number} [props.controlHeight]
 * @param {number} [props.menuMaxHeight] — alto máximo del área scrolleable de opciones
 * @param {boolean} [props.showClear]
 * @param {string} [props.emptySearchMessage]
 * @param {(selected: string[], options: object[]) => string} [props.renderValue]
 * @param {object} [props.sx] — estilos del contenedor raíz (p. ej. flex: 1)
 */
export default function SearchableMultiSelect({
  id,
  options = [],
  value = [],
  onChange,
  placeholder = 'Seleccionar',
  searchPlaceholder = 'Buscar...',
  disabled = false,
  loading = false,
  controlHeight = DEFAULT_CONTROL_HEIGHT,
  menuMaxHeight = DEFAULT_MENU_MAX_HEIGHT,
  showClear = true,
  emptySearchMessage = 'Sin resultados',
  renderValue,
  sx = undefined,
}) {
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const [resolvedListMaxHeight, setResolvedListMaxHeight] = useState(menuMaxHeight);
  const [search, setSearch] = useState('');

  const selected = useMemo(
    () => (Array.isArray(value) ? value.filter(Boolean) : []),
    [value],
  );

  const displayLabel = useMemo(() => {
    if (typeof renderValue === 'function') return renderValue(selected, options);
    if (selected.length === 0) return placeholder;
    if (selected.length === 1) {
      return options.find((o) => o.value === selected[0])?.label ?? '1 seleccionado';
    }
    return `${selected.length} seleccionados`;
  }, [renderValue, selected, options, placeholder]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => String(o.label || '').toLowerCase().includes(term));
  }, [options, search]);

  const toggleOption = (optionValue) => {
    onChange?.(
      selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue],
    );
  };

  const handleOpen = () => {
    if (disabled || loading) return;
    const anchorEl = triggerRef.current;
    setMenuWidth(anchorEl?.offsetWidth ?? null);
    setResolvedListMaxHeight(getAvailableMenuListHeight(anchorEl, menuMaxHeight));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearch('');
  };

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => searchRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const syncMenuHeight = () => {
      setResolvedListMaxHeight(
        getAvailableMenuListHeight(triggerRef.current, menuMaxHeight),
      );
    };

    window.addEventListener('resize', syncMenuHeight);
    window.addEventListener('scroll', syncMenuHeight, true);
    return () => {
      window.removeEventListener('resize', syncMenuHeight);
      window.removeEventListener('scroll', syncMenuHeight, true);
    };
  }, [open, menuMaxHeight]);

  const hasSelection = selected.length > 0;

  return (
    <Box ref={triggerRef} sx={{ position: 'relative', minWidth: 0, ...sx }}>
      <Box
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-disabled={disabled || loading}
        onClick={handleOpen}
        sx={{
          height: controlHeight,
          minHeight: controlHeight,
          px: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          backgroundColor: '#242424',
          borderRadius: '8px',
          border: `1px solid ${open ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)'}`,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          opacity: disabled || loading ? 0.5 : 1,
          userSelect: 'none',
          transition: 'border-color 0.15s, background 0.15s',
          '&:hover': disabled || loading ? {} : {
            borderColor: open ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.20)',
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: typography.fontFamily.primary,
            fontSize: '13px',
            color: hasSelection ? '#ffffff' : 'rgba(255,255,255,0.55)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayLabel}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: 'rgba(255,255,255,0.65)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={VIEWPORT_PADDING}
        slotProps={{
          ...POPOVER_POPPER_SLOT_PROPS,
          paper: {
            sx: {
              mt: 0.75,
              bgcolor: colors.backgroundSurface,
              color: colors.textPrimary,
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: `calc(100vh - ${VIEWPORT_PADDING * 2}px)`,
              ...(menuWidth
                ? { width: menuWidth, minWidth: menuWidth, maxWidth: menuWidth }
                : {}),
            },
          },
        }}
      >
        <Box
          sx={{ p: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            component="input"
            ref={searchRef}
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              width: '100%',
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '12px',
              padding: '5px 8px',
              outline: 'none',
              fontFamily: typography.fontFamily.primary,
              boxSizing: 'border-box',
              '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
              '&:focus': { borderColor: 'rgba(255,255,255,0.22)' },
            }}
          />
        </Box>

        <Box
          role="listbox"
          aria-multiselectable="true"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: resolvedListMaxHeight,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.15) transparent',
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 99,
            },
            py: '4px',
          }}
        >
          {filteredOptions.length === 0 ? (
            <Typography sx={{ px: 1.5, py: 1.25, fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
              {emptySearchMessage}
            </Typography>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <Box
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  sx={optionRowSx(isSelected)}
                  onClick={() => toggleOption(option.value)}
                >
                  <Checkbox size="small" checked={isSelected} tabIndex={-1} sx={checkboxSx} />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{
                      fontSize: '13px',
                      color: '#ffffff',
                      noWrap: true,
                    }}
                    sx={{ minWidth: 0, m: 0 }}
                  />
                </Box>
              );
            })
          )}
        </Box>

        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderTop: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
            {options.length} opción{options.length !== 1 ? 'es' : ''}
          </Typography>
          {showClear && hasSelection && (
            <Box
              component="button"
              type="button"
              onClick={() => onChange?.([])}
              sx={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                p: 0,
                fontFamily: typography.fontFamily.primary,
                '&:hover': { color: 'rgba(255,255,255,0.75)' },
              }}
            >
              Limpiar selección
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
}
